/**
 * MessageList Component
 *
 * Scrollable container for chat messages.
 * Phase 7: Frontend Integration
 *
 * Batch 2: sticky-only autoscroll — the viewport follows new messages ONLY
 * when the user is already near the bottom, so scrolling up to read history is
 * no longer yanked back down. A "scroll to latest" button appears while the
 * user is scrolled up.
 *
 * @module MessageList
 */

'use client';

import { useRef, useEffect, useState, useCallback } from 'react';
import { Box, VStack, Text, Flex, IconButton } from '@axon/ui';
import { useTranslations } from 'next-intl';
import { LuMessageCircle, LuArrowDown } from 'react-icons/lu';

import { useChatbotContext } from '../providers/ChatbotProvider';

import { MessageItem } from './MessageItem';

// ============================================================================
// Constants
// ============================================================================

/** Distance (px) from the bottom within which autoscroll stays "stuck". */
const STICK_THRESHOLD_PX = 80;

// ============================================================================
// Component
// ============================================================================

/**
 * MessageList component
 *
 * Displays all chat messages with sticky auto-scroll behavior.
 *
 * @example
 * <MessageList />
 */
export function MessageList() {
  const { messages } = useChatbotContext();
  const t = useTranslations('chatbot');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  // Mirror stickiness in a ref so the messages effect can read the latest value
  // without listing isAtBottom as a dependency (which would re-run on scroll).
  const isAtBottomRef = useRef(true);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
    isAtBottomRef.current = true;
    setIsAtBottom(true);
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distanceFromBottom <= STICK_THRESHOLD_PX;
    isAtBottomRef.current = atBottom;
    setIsAtBottom(atBottom);
  }, []);

  // Follow new messages/chunks only when the user is already near the bottom.
  useEffect(() => {
    if (isAtBottomRef.current) {
      scrollToBottom('auto');
    }
  }, [messages, scrollToBottom]);

  return (
    <Box position="relative" height="full">
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        // a11y: announce new messages to screen readers as a chat log.
        role="log"
        aria-live="polite"
        aria-relevant="additions text"
        aria-label={t('messagesRegion' as Parameters<typeof t>[0])}
        height="full"
        overflowY="auto"
        padding="4"
        css={{
          // Custom scrollbar styling (semantic tokens so it adapts to dark mode)
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'border.emphasized',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'border.muted',
          },
        }}
      >
        {messages.length === 0 ? (
          // Empty state
          <Flex
            direction="column"
            align="center"
            justify="center"
            height="full"
            color="fg.muted"
            textAlign="center"
            paddingX="6"
          >
            <LuMessageCircle size={48} />
            <Text marginTop="4" fontSize="md" fontWeight="medium">
              {t('emptyState.title')}
            </Text>
            <Text marginTop="1" fontSize="sm">
              {t('emptyState.subtitle')}
            </Text>
          </Flex>
        ) : (
          // Message list
          <VStack gap="2" align="stretch">
            {messages.map((message) => (
              <MessageItem key={message.id} message={message} />
            ))}
          </VStack>
        )}
      </Box>

      {/* Scroll-to-latest affordance — only while scrolled up with messages.
          IconButton (extends ButtonHTMLAttributes) takes no Chakra layout
          props, so positioning goes on the Box and the round/shadow look via
          the HTML `style` prop. */}
      {!isAtBottom && messages.length > 0 && (
        <Box position="absolute" bottom="3" insetEnd="3">
          <IconButton
            aria-label={t('scrollToBottom' as Parameters<typeof t>[0])}
            icon={<LuArrowDown size={16} />}
            size="sm"
            variant="primary"
            colorPalette="blue"
            style={{ borderRadius: '9999px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)' }}
            onClick={() => scrollToBottom('smooth')}
          />
        </Box>
      )}
    </Box>
  );
}

export default MessageList;
